import { CalendarDays, Pencil, ScanLine, Upload, UserMinus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { EmptyState } from '@/components/EmptyState'
import { useConfirm } from '@/components/patterns/useConfirm'
import { Button } from '@/components/ui/button'
import { NativeSelect } from '@/components/ui/native-select'
import { SourceTag } from '@/components/ui/source-tag'
import { formatDatum } from '@/lib/format'
import { runWithFeedback, toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { toastDeferredDelete } from '@/lib/toastUndo'
import { ContactCardList } from '../ContactCardList'
import { contactColumns } from '../contactColumns'
import { todayIso } from '../dates'
import { AdvancedFilterSheet } from '../filters/AdvancedFilterSheet'
import { useFilterOptions } from '../filters/useFilterOptions'
import { BulkMenus } from '../list/BulkMenus'
import { RecordListPage, type RecordListConfig } from '../list/RecordListPage'
import { countRecords, getRegister } from '../query/api'
import { normalizeFilter } from '../query/codec'
import { describeCondition } from '../query/describe'
import type { ContactRow, Filter, MemberSource, Register } from '../query/types'
import { AddMembersMenu } from './AddMembersMenu'
import { removeListMembers, updateListV2 } from './api'
import type { ListDetail } from './types'

const noun = (count: number): string => (count === 1 ? '1 contact' : `${count} contacten`)
const MAX_REMOVE = 500

const SORT_OPTIONS = [
  { value: 'name', label: 'Naam (A–Z)' },
  { value: '-last_activity_at', label: 'Laatst gewijzigd (nieuwste eerst)' },
  { value: 'last_contact_at', label: 'Laatste contact (oudste eerst)' },
  { value: 'next_action_on', label: 'Volgende actie' },
] as const

const SOURCES: readonly { value: MemberSource; label: string; icon: typeof Pencil }[] = [
  { value: 'handmatig', label: 'Handmatig', icon: Pencil },
  { value: 'scan', label: 'Scan', icon: ScanLine },
  { value: 'import', label: 'Import', icon: Upload },
  { value: 'event', label: 'Event', icon: CalendarDays },
]

function SourceCell({ row }: { row: ContactRow }) {
  const source = SOURCES.find((candidate) => candidate.value === row.member?.source)
  if (!source) return <span className="text-muted-foreground">–</span>
  const Icon = source.icon
  return <SourceTag icon={<Icon />}>{source.label}</SourceTag>
}

function AddedCell({ row }: { row: ContactRow }) {
  return row.member ? <span className="whitespace-nowrap text-muted-foreground">{formatDatum(row.member.added_at)}</span> : <span className="text-muted-foreground">–</span>
}

/** What a dynamic list's filter says, as one line, and the way to change it. */
function FilterSummary({ list, onChanged }: { list: ListDetail; onChanged: () => void }) {
  const [register, setRegister] = useState<Register | null>(null)
  const [open, setOpen] = useState(false)
  const filter: Filter = list.filter ? normalizeFilter(list.filter) : normalizeFilter({})
  const options = useFilterOptions(register, filter)
  useEffect(() => {
    let stale = false
    getRegister('contacten').then(
      (found) => {
        if (!stale) setRegister(found)
      },
      (caught: unknown) => {
        if (!stale) toastFailure(caught, 'Het filter laden mislukt.')
      },
    )
    return () => {
      stale = true
    }
  }, [])

  const text = (condition: Filter['quick'][number]): string =>
    describeCondition(condition, register?.properties.find((property) => property.key === condition.property), { names: options.names, today: todayIso() })
  const parts = [...filter.quick.map(text), ...filter.groups.map((group) => group.map(text).join(' en '))]

  async function apply(next: Filter) {
    setOpen(false)
    const saved = await runWithFeedback(() => updateListV2(list.id, { filter: next }), { error: 'Het filter opslaan mislukt.' })
    if (!saved) return
    toast.success('Filter bijgewerkt.')
    onChanged()
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm">
      <p className="min-w-0 flex-1">
        <span className="font-semibold">Filter: </span>
        {register === null ? 'laden…' : parts.length === 0 ? 'alle contacten' : parts.join(' · ')}
      </p>
      {register ? (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          Filter bewerken
        </Button>
      ) : null}
      {register ? (
        <AdvancedFilterSheet
          open={open}
          onOpenChange={setOpen}
          register={register}
          filter={filter}
          options={options}
          today={todayIso()}
          noun={noun}
          count={(draft) => countRecords('contacten', draft)}
          onApply={(next) => void apply(next)}
        />
      ) : null}
    </div>
  )
}

/**
 * The members of a list (spec-63 E5, design §4.7): the list page of Contacten with the list as a locked
 * condition and `list_context` in every search and export (so rows carry Bron and Toegevoegd). A static list can
 * add and remove members, one at a time with an undo or several at once after a question; a dynamic list shows
 * its filter and can only have that changed.
 */
export function ListMembers({ list, refresh, onChanged }: { list: ListDetail; refresh: number; onChanged: () => void }) {
  const isStatic = list.type === 'static'
  const editable = isStatic && list.archived_at === null
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const [source, setSource] = useState<MemberSource | ''>('')
  const [dialog, confirm] = useConfirm()

  const added = () => {
    setHidden(new Set())
    onChanged()
  }

  function takeOff(row: ContactRow) {
    const toggle = (on: boolean) =>
      setHidden((current) => {
        const next = new Set(current)
        if (on) next.add(row.person_id)
        else next.delete(row.person_id)
        return next
      })
    toastDeferredDelete({
      message: `${row.full_name} uit lijst gehaald.`,
      hide: () => toggle(true),
      restore: () => toggle(false),
      commit: async () => {
        await removeListMembers(list.id, [row.person_id])
        onChanged()
      },
      error: 'Uit de lijst halen mislukt.',
    })
  }

  async function takeOffSelected(ids: string[], done: (() => void) | undefined) {
    const ok = await confirm({
      title: `${noun(ids.length)} uit '${list.name}' halen?`,
      description: 'De contacten zelf blijven bestaan; ze staan alleen niet meer op deze lijst.',
      confirmLabel: 'Uit lijst halen',
      destructive: false,
    })
    if (!ok) return
    const result = await runWithFeedback(() => removeListMembers(list.id, ids), { error: 'Uit de lijst halen mislukt.' })
    if (!result) return
    toast.success(`${noun(result.removed)} uit de lijst gehaald.`)
    done?.()
    onChanged()
  }

  const addButton = editable ? <AddMembersMenu listId={list.id} listName={list.name} onAdded={added} /> : null

  const config: RecordListConfig<ContactRow> = {
    object: 'contacten',
    title: 'Leden',
    subtitle: '',
    searchPlaceholder: 'Zoek in deze lijst…',
    searchLabel: 'Zoek leden',
    noun,
    rowKey: (row) => row.person_id,
    rowLabel: (row) => row.full_name,
    columns: contactColumns(),
    cardList: (rows, sort) => <ContactCardList rows={rows} sort={sort} />,
    sortOptions: SORT_OPTIONS,
    fixedTabs: true,
    hideHeader: true,
    rememberView: false,
    headerActions: () => null,
    locked: [{ property: 'lists', operator: 'contains_any_of', value: [list.id] }],
    lockedChips: [`Lijst: ${list.name}`],
    extraBody: { list_context: list.id, ...(isStatic && source ? { member_sources: [source] } : {}) },
    reloadToken: refresh,
    hiddenKeys: hidden,
    extraColumns: isStatic
      ? [
          { key: 'member_source', column: { header: 'Bron', cell: (row) => <SourceCell row={row} /> } },
          { key: 'member_added_at', sortable: true, column: { header: 'Toegevoegd', cell: (row) => <AddedCell row={row} /> } },
        ]
      : undefined,
    toolbar: isStatic
      ? () => (
          <label className="flex items-center gap-2 text-sm font-semibold">
            Bron
            <NativeSelect className="w-40" value={source} onChange={(event) => setSource(event.target.value as MemberSource | '')}>
              <option value="">Alle bronnen</option>
              {SOURCES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </NativeSelect>
          </label>
        )
      : undefined,
    rowActions: editable
      ? (row) => (
          <Button type="button" variant="ghost" size="icon-sm" aria-label={`${row.full_name} uit de lijst halen`} title="Uit lijst halen" onClick={() => takeOff(row)}>
            <UserMinus />
          </Button>
        )
      : undefined,
    bulkMenus: (props) => (
      <>
        <BulkMenus object="contacten" {...props} />
        {editable ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!props.selected || props.selected.all || props.selected.ids.length > MAX_REMOVE}
            title={props.selected?.all ? 'Kies hooguit 500 rijen om uit de lijst te halen' : undefined}
            onClick={() => void takeOffSelected(props.selected?.ids ?? [], props.onDone)}
          >
            Uit lijst halen
          </Button>
        ) : null}
      </>
    ),
    empty: isStatic ? (
      <EmptyState
        variant="bare"
        title="Nog geen leden."
        description="Voeg ze toe of scan visitekaartjes."
        action={editable ? addButton : undefined}
      />
    ) : (
      <EmptyState variant="bare" title="Niemand voldoet nu aan het filter." />
    ),
    exportFilename: 'lijst.xlsx',
  }

  const showHeaderAdd = editable && (list.members_count ?? 0) > 0
  return (
    <section aria-labelledby="list-members" className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="list-members" className="font-heading text-lg font-semibold">
          {list.members_count === null ? 'Leden' : `Leden (${list.members_count})`}
        </h2>
        {showHeaderAdd ? addButton : null}
      </div>
      {!isStatic ? <FilterSummary list={list} onChanged={onChanged} /> : null}
      <RecordListPage config={config} />
      {dialog}
    </section>
  )
}
